import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import {
  PROSPECT_STATUSES,
  formatDate,
  formatProspectStatus,
} from "../../../constants/automapDealerOnboardingConstants";
import {
  fetchAdminDealerProspects,
  trackDealerEvent,
} from "../../../services/automapDealerOnboardingApi";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { AdminDealerNav } from "./dealerAdminShared";

function AdminDealerProspectsListPageContent() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      const data = await fetchAdminDealerProspects(params);
      setProspects(data.prospects || []);
    } catch (err) {
      setError(err.message);
      setProspects([]);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { load(); trackDealerEvent("admin_dealer_prospect_viewed"); }, [load]);

  return (
    <AdminPageLayout>
      <AdminAutomotiveNav active="dealers" />
      <AdminPageHeader
        eyebrow="AutoMapa · Prospectos"
        title="Prospectos de concesionarios"
        navigation={<AdminDealerNav active="prospects" />}
      />
      <main className="admin-console__main">
        <div className="admin-filters" style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
          <input type="search" placeholder="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Estado</option>
            {PROSPECT_STATUSES.map((s) => <option key={s} value={s}>{formatProspectStatus(s)}</option>)}
          </select>
        </div>
        {error ? <AdminStatePanel variant="empty" message={error} /> : null}
        {loading ? <AdminStatePanel variant="empty" message="Cargando…" /> : prospects.length === 0 ? (
          <AdminStatePanel variant="empty" message="Sin prospectos." />
        ) : (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Negocio</th><th>Contacto</th><th>Ciudad</th><th>Inventario est.</th><th>Estado</th><th>Creado</th><th />
                </tr>
              </thead>
              <tbody>
                {prospects.map((p) => (
                  <tr key={p.id}>
                    <td>{p.businessName}</td>
                    <td>{p.contactName}</td>
                    <td>{p.city}</td>
                    <td>{p.estimatedInventoryCount ?? "—"}</td>
                    <td>{formatProspectStatus(p.status)}</td>
                    <td>{formatDate(p.createdAt)}</td>
                    <td><Link to={`/admin/dealers/prospects/${p.id}`}>Ver</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminDealerProspectsListPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealerProspectsListPageContent />
    </RequirePermission>
  );
}

function AdminDealerProspectDetailPageContent() {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { fetchAdminDealerProspect } = await import("../../../services/automapDealerOnboardingApi");
        const data = await fetchAdminDealerProspect(prospectId);
        setProspect(data.prospect);
        setNotes(data.prospect?.notes || "");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [prospectId]);

  const convert = async () => {
    const { convertAdminDealerProspect, trackDealerEvent } = await import("../../../services/automapDealerOnboardingApi");
    const data = await convertAdminDealerProspect(prospectId, { internalNotes: notes });
    trackDealerEvent("admin_dealer_prospect_converted", { prospectId, dealerId: data.dealer?.id });
    navigate(`/admin/dealers/${data.dealer.id}`);
  };

  const updateStatus = async (status) => {
    const { updateAdminDealerProspect } = await import("../../../services/automapDealerOnboardingApi");
    const data = await updateAdminDealerProspect(prospectId, { status, notes });
    setProspect(data.prospect);
  };

  if (loading) return <AdminPageLayout><AdminStatePanel variant="empty" message="Cargando…" /></AdminPageLayout>;
  if (error || !prospect) return <AdminPageLayout><AdminStatePanel variant="empty" message={error || "No encontrado"} /></AdminPageLayout>;

  return (
    <AdminPageLayout>
      <AdminAutomotiveNav active="dealers" />
      <AdminPageHeader title={prospect.businessName} navigation={<AdminDealerNav active="prospects" />} />
      <main className="admin-console__main">
        <p>Contacto: {prospect.contactName} · {prospect.whatsapp} · {prospect.email || "—"}</p>
        <p>Ciudad: {prospect.city} · {prospect.address || ""}</p>
        <p>Inventario estimado: {prospect.estimatedInventoryCount ?? "—"} · Formato: {prospect.inventoryFormat || "—"}</p>
        <p>Financiación: {prospect.offersFinancing || "—"}</p>
        <p>Canales: {(prospect.currentListingChannels || []).join(", ") || "—"}</p>
        <p>Estado: {formatProspectStatus(prospect.status)}</p>
        <p>Mensaje: {prospect.message || "—"}</p>
        {prospect.convertedDealerId ? (
          <p><Link to={`/admin/dealers/${prospect.convertedDealerId}`}>Ver concesionario convertido</Link></p>
        ) : null}
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas internas" style={{ width: "100%", minHeight: 80 }} />
        {canManage ? (
          <div className="admin-console__header-actions" style={{ marginTop: "1rem" }}>
            <button type="button" className="admin-btn" onClick={() => updateStatus("contacted")}>Marcar contactado</button>
            <button type="button" className="admin-btn" onClick={() => updateStatus("qualified")}>Calificar</button>
            <button type="button" className="admin-btn" onClick={() => updateStatus("not_fit")}>No califica</button>
            {prospect.status !== "converted_to_dealer" ? (
              <button type="button" className="admin-btn admin-btn--primary" onClick={convert}>Convertir a concesionario</button>
            ) : null}
          </div>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}

export function AdminDealerProspectDetailPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealerProspectDetailPageContent />
    </RequirePermission>
  );
}
